import { FormEvent, useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import AppLayout from '../../components/app-layout/AppLayout'
import { useAuth } from '../../_hooks/use-auth'
import { submitSupportRequest } from '../../_data/support'
import { SupportRequestCategory } from '../../_types/support'
import { APP_SUPPORT_CATEGORIES } from './helpers'
import { HelpPageState } from './types'
import {
  ActionArrow,
  ActionButton,
  ActionStack,
  BackButton,
  CategoryChip,
  CategoryRow,
  ChoiceDescription,
  ChoiceGrid,
  ChoiceIcon,
  ChoiceTile,
  ChoiceTitle,
  ErrorBanner,
  Form,
  Heading,
  Label,
  MessageTextarea,
  SectionCard,
  SectionIntro,
  Subtext,
  SubmitButton,
  SuccessIcon,
  Wrapper,
} from './styled'

const INITIAL_STATE: HelpPageState = {
  view: 'triage',
  category: 'cant-find',
  message: '',
  status: 'idle',
  errorMessage: '',
}

/**
 * Tenant help page. Triages "what do you want help with?" into two paths:
 * the renter's HOME (repairs, lease, building) routes to their property
 * manager via the tools that already exist (maintenance, messages); the APP
 * (trouble using QuickPass itself) routes straight to the QuickCasa team — so
 * the property manager never fields "I can't find the button" messages.
 *
 * @returns {JSX.Element}
 */
const HelpPage = (): JSX.Element => {
  const navigate = useNavigate()
  const { quickPassUser } = useAuth()
  const [state, setState] = useState<HelpPageState>(INITIAL_STATE)

  const goToTriage = useCallback((): void => {
    setState(INITIAL_STATE)
  }, [])

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault()

      if (quickPassUser === null || state.message.trim() === '') {
        return
      }

      setState(previous => ({ ...previous, status: 'submitting' }))

      const didSubmit = await submitSupportRequest({
        quickPassUserId: quickPassUser.id,
        category: state.category,
        message: state.message.trim(),
      })

      setState(previous => ({
        ...previous,
        status: didSubmit ? 'submitted' : 'error',
        errorMessage: didSubmit
          ? ''
          : 'We couldn’t send that just now — please try again.',
      }))
    },
    [quickPassUser, state.category, state.message],
  )

  return (
    <AppLayout contentMaxWidth="narrow">
      <Wrapper>
        <Heading>How can we help?</Heading>
        <Subtext>
          Tell us what you’re stuck on and we’ll point you the right way.
        </Subtext>

        {state.view === 'triage' ? (
          <ChoiceGrid>
            <ChoiceTile
              type="button"
              onClick={() =>
                setState(previous => ({ ...previous, view: 'home' }))
              }
            >
              <ChoiceIcon role="img" aria-label="House">
                🏠
              </ChoiceIcon>
              <ChoiceTitle>My home</ChoiceTitle>
              <ChoiceDescription>
                Repairs, your lease, rent, or anything about your place.
              </ChoiceDescription>
            </ChoiceTile>
            <ChoiceTile
              type="button"
              onClick={() =>
                setState(previous => ({ ...previous, view: 'app' }))
              }
            >
              <ChoiceIcon role="img" aria-label="Mobile phone">
                📱
              </ChoiceIcon>
              <ChoiceTitle>The app</ChoiceTitle>
              <ChoiceDescription>
                Trouble using QuickPass itself — finding things, sign-in, bugs.
              </ChoiceDescription>
            </ChoiceTile>
          </ChoiceGrid>
        ) : null}

        {state.view === 'home' ? (
          <SectionCard>
            <SectionIntro>
              Your property manager looks after your home. Here’s the quickest
              way to reach them.
            </SectionIntro>
            <ActionStack>
              <ActionButton
                type="button"
                onClick={() => navigate('/maintenance')}
              >
                <span>Report a maintenance issue</span>
                <ActionArrow aria-hidden="true">→</ActionArrow>
              </ActionButton>
              <ActionButton type="button" onClick={() => navigate('/messages')}>
                <span>Message your property manager</span>
                <ActionArrow aria-hidden="true">→</ActionArrow>
              </ActionButton>
            </ActionStack>
            <BackButton type="button" onClick={goToTriage}>
              ← Back
            </BackButton>
          </SectionCard>
        ) : null}

        {state.view === 'app' && state.status === 'submitted' ? (
          <SectionCard>
            <SuccessIcon aria-hidden="true">✓</SuccessIcon>
            <Heading>Got it — thanks!</Heading>
            <SectionIntro>
              Your message went straight to the QuickPass team. We’ll be in
              touch by email. Your property manager won’t be bothered with this.
            </SectionIntro>
            <BackButton type="button" onClick={goToTriage}>
              ← Back to help
            </BackButton>
          </SectionCard>
        ) : null}

        {state.view === 'app' && state.status !== 'submitted' ? (
          <SectionCard>
            <SectionIntro>
              This goes straight to the QuickPass team — not your property
              manager. Tell us what’s going on and we’ll sort it out.
            </SectionIntro>
            <Form onSubmit={handleSubmit}>
              <Label>What’s up?</Label>
              <CategoryRow>
                {APP_SUPPORT_CATEGORIES.map(option => (
                  <CategoryChip
                    key={option.value}
                    type="button"
                    aria-pressed={state.category === option.value}
                    onClick={() =>
                      setState(previous => ({
                        ...previous,
                        category: option.value as SupportRequestCategory,
                      }))
                    }
                  >
                    {option.label}
                  </CategoryChip>
                ))}
              </CategoryRow>

              <Label>Tell us more</Label>
              <MessageTextarea
                placeholder="What were you trying to do? What happened?"
                value={state.message}
                onChange={event =>
                  setState(previous => ({
                    ...previous,
                    message: event.target.value,
                  }))
                }
                disabled={state.status === 'submitting'}
              />

              {state.status === 'error' ? (
                <ErrorBanner role="alert">{state.errorMessage}</ErrorBanner>
              ) : null}

              <SubmitButton
                type="submit"
                disabled={
                  state.status === 'submitting' || state.message.trim() === ''
                }
              >
                {state.status === 'submitting'
                  ? 'Sending…'
                  : 'Send to QuickPass'}
              </SubmitButton>
            </Form>
            <BackButton type="button" onClick={goToTriage}>
              ← Back
            </BackButton>
          </SectionCard>
        ) : null}
      </Wrapper>
    </AppLayout>
  )
}

export default HelpPage
